import axios from "axios";
import type {
  BufferLike,
  CreateDirectoryOptions,
  CreateReadStreamOptions,
  CreateWriteStreamCallback,
  CreateWriteStreamOptions,
  DiskQuota,
  FileStat,
  GetDirectoryContentsOptions,
  GetFileContentsOptions,
  GetQuotaOptions,
  Headers,
  LockResponse,
  WebDAVClient as OriginalWebDAVClient,
  PutFileContentsOptions,
  ResponseDataDetailed,
  StatOptions,
  WebDAVMethodOptions,
} from "webdav";

export interface WebDAVResponse {
  success: boolean;
  message: string;
}

interface CustomAxiosRequestHeaders {
  "Content-Type": string;
  Destination: string;
  Authorization: string;
}

export class ExtendedWebDAVClient implements OriginalWebDAVClient {
  private readonly client: OriginalWebDAVClient;
  private readonly baseUrl: string;

  constructor(client: OriginalWebDAVClient, baseUrl: string) {
    this.client = client;
    this.baseUrl = baseUrl;

    this.bindMethods();
  }

  private bindMethods(): void {
    this.copyFile = this.copyFile.bind(this);
    this.moveFile = this.moveFile.bind(this);
    this.deleteFile = this.deleteFile.bind(this);
    this.createDirectory = this.createDirectory.bind(this);
    this.createReadStream = this.createReadStream.bind(this);
    this.createWriteStream = this.createWriteStream.bind(this);
    this.exists = this.exists.bind(this);
    this.getDirectoryContents = this.getDirectoryContents.bind(this);
    this.getFileContents = this.getFileContents.bind(this);
    this.getFileDownloadLink = this.getFileDownloadLink.bind(this);
    this.getFileUploadLink = this.getFileUploadLink.bind(this);
    this.getHeaders = this.getHeaders.bind(this);
    this.getQuota = this.getQuota.bind(this);
    this.lock = this.lock.bind(this);
    this.putFileContents = this.putFileContents.bind(this);
    this.setHeaders = this.setHeaders.bind(this);
    this.stat = this.stat.bind(this);
    this.unlock = this.unlock.bind(this);
    this.unzipFile = this.unzipFile.bind(this);
    this.zipInPlace = this.zipInPlace.bind(this);
  }

  private ensureClientInitialized(): void {
    if (!this.client) {
      throw new Error("WebDAV client is not initialized");
    }
  }

  async copyFile(filename: string, destination: string): Promise<void> {
    this.ensureClientInitialized();
    return this.client.copyFile(filename, destination);
  }

  async moveFile(filename: string, destination: string): Promise<void> {
    this.ensureClientInitialized();
    return this.client.moveFile(filename, destination);
  }

  async deleteFile(filename: string): Promise<void> {
    this.ensureClientInitialized();
    return this.client.deleteFile(filename);
  }

  async createDirectory(path: string, options?: CreateDirectoryOptions): Promise<void> {
    this.ensureClientInitialized();
    return this.client.createDirectory(path, options);
  }

  createReadStream(filename: string, options?: CreateReadStreamOptions) {
    this.ensureClientInitialized();
    return this.client.createReadStream(filename, options);
  }

  createWriteStream(filename: string, options?: CreateWriteStreamOptions, callback?: CreateWriteStreamCallback) {
    this.ensureClientInitialized();
    return this.client.createWriteStream(filename, options, callback);
  }

  async exists(path: string): Promise<boolean> {
    this.ensureClientInitialized();
    return this.client.exists(path);
  }

  async getDirectoryContents(
    path: string,
    options?: GetDirectoryContentsOptions,
  ): Promise<Array<FileStat> | ResponseDataDetailed<Array<FileStat>>> {
    this.ensureClientInitialized();
    return this.client.getDirectoryContents(path, options);
  }

  async getFileContents(
    filename: string,
    options?: GetFileContentsOptions,
  ): Promise<BufferLike | string | ResponseDataDetailed<BufferLike | string>> {
    this.ensureClientInitialized();
    return this.client.getFileContents(filename, options);
  }

  getFileDownloadLink(filename: string): string {
    this.ensureClientInitialized();
    return this.client.getFileDownloadLink(filename);
  }

  getFileUploadLink(filename: string): string {
    this.ensureClientInitialized();
    return this.client.getFileUploadLink(filename);
  }

  getHeaders(): Headers {
    this.ensureClientInitialized();
    return this.client.getHeaders();
  }

  async getQuota(options?: GetQuotaOptions): Promise<DiskQuota | ResponseDataDetailed<DiskQuota | null> | null> {
    this.ensureClientInitialized();
    return this.client.getQuota(options);
  }

  async lock(path: string, options?: WebDAVMethodOptions): Promise<LockResponse> {
    this.ensureClientInitialized();
    return this.client.lock(path, options);
  }

  async putFileContents(
    filename: string,
    data: string | BufferLike | unknown,
    options?: PutFileContentsOptions,
  ): Promise<boolean> {
    this.ensureClientInitialized();
    return this.client.putFileContents(filename, data as BufferLike, options);
  }

  setHeaders(headers: Headers): void {
    this.ensureClientInitialized();
    return this.client.setHeaders(headers);
  }

  async stat(path: string, options?: StatOptions): Promise<FileStat | ResponseDataDetailed<FileStat>> {
    this.ensureClientInitialized();
    return this.client.stat(path, options);
  }

  async unlock(path: string, token: string, options?: WebDAVMethodOptions): Promise<void> {
    this.ensureClientInitialized();
    return this.client.unlock(path, token, options);
  }

  async unzipFile(filename: string, destination: string): Promise<WebDAVResponse> {
    const formData = new URLSearchParams();
    formData.append("path_prefix", filename);

    const urlWithAction = this.buildUrl(filename, "unzip");
    const headers = this.constructHeaders(destination.substring(0, destination.lastIndexOf("/")));

    return this.postRequest(urlWithAction, formData, headers, "Files successfully unzipped");
  }

  async zipInPlace(filenames: string[], destination: string): Promise<WebDAVResponse> {
    const formData = new URLSearchParams();
    filenames.forEach((filename) => formData.append("path_prefix", filename));

    const urlWithAction = this.buildUrl("", "zip_in_place");
    const headers = this.constructHeaders(destination);

    return this.postRequest(urlWithAction, formData, headers, `Files successfully zipped into ${destination}`);
  }

  private buildUrl(path: string, action: string): string {
    const url = new URL(this.baseUrl + path);
    url.searchParams.append("action", action);
    return url.toString();
  }

  private constructHeaders(destination: string): CustomAxiosRequestHeaders {
    const authHeader = this.client.getHeaders()?.Authorization?.trim() ?? "Unauthorized";
    return {
      "Content-Type": "application/x-www-form-urlencoded",
      Destination: destination,
      Authorization: authHeader,
    };
  }

  private async postRequest(
    url: string,
    formData: URLSearchParams,
    headers: CustomAxiosRequestHeaders,
    successMessage: string,
  ): Promise<WebDAVResponse> {
    try {
      await axios.post(url, formData, { headers });
      return { success: true, message: successMessage };
    } catch (error) {
      return { success: false, message: this.formatZipError(error) };
    }
  }

  private formatZipError(error: {
    response?: {
      status: number;
      statusText: string;
      data: string;
    };
    request?: {
      headers: CustomAxiosRequestHeaders;
    };
    message?: string;
  }): string {
    if (error.response) {
      if (error.response.status === 500) {
        return error.response.data;
      }
      if (error.response.status === 404) {
        return `No files found to zip: HTTP ${error.response.status} ${error.response.statusText}`;
      }
      return `An unknown error occurred: ${error.response.status}: ${error.response.data}`;
    } else if (error.request) {
      return "No response received from the server.";
    } else {
      return `Error: ${error.message}`;
    }
  }
}
